import type { Config, CustomPlugin } from 'svgo/browser';
import { isJavascriptUrl } from '@/utils/svg-tools/svg-source';

// SVGO settings for the optimizer on /svg-viewer. The page imports svgo/browser only when
// the visitor presses Optimize; this module holds just the config so it can be tested in Node.

export interface OptimizeOptions {
  /** Keep ids as they are, for CSS or scripts outside the SVG that refer to them. */
  keepIds: boolean;
  /** Strip <script>, on* handlers and javascript: URLs. */
  removeScripts: boolean;
  /** Indent the output instead of writing it on one line. */
  pretty: boolean;
}

export const defaultOptimizeOptions: OptimizeOptions = { keepIds: false, removeScripts: true, pretty: false };

// SVGO's removeScripts only checks href on <a>. This also drops javascript: URLs from every
// other attribute, such as the src of an <iframe> inside <foreignObject>.
const removeJavascriptUrls: CustomPlugin = {
  name: 'removeJavascriptUrls',
  fn: () => ({
    element: {
      enter: (node) => {
        for (const [name, value] of Object.entries(node.attributes)) {
          if (isJavascriptUrl(value)) delete node.attributes[name];
        }
      },
    },
  }),
};

/**
 * SVGO 4's preset-default, which is meant not to change how the SVG looks: it keeps the
 * viewBox and <title>, rounds coordinates to 3 decimals and removes comments, editor
 * metadata and default values. Multipass repeats it until the file stops shrinking.
 */
export function svgoConfig(options: OptimizeOptions): Config {
  return {
    multipass: true,
    js2svg: options.pretty ? { pretty: true, indent: 2 } : undefined,
    plugins: [
      { name: 'preset-default', params: { overrides: options.keepIds ? { cleanupIds: false } : {} } },
      ...(options.removeScripts ? (['removeScripts', removeJavascriptUrls] as const) : []),
    ],
  };
}
